import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Video,
  Zap,
  Loader2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Camera,
  Clock,
  Volume2
} from "lucide-react";
import { toast } from "sonner";
import EmaLogo from "../assets/ema-logo.png";

import LinkExpired from "../components/LinkExpired";
import { robustFetch, getNetworkQuality, isSlowNetwork } from "../lib/robustFetch";
const API = process.env.REACT_APP_BACKEND_URL;

// Interview duration constants (in seconds)
const VIDEO_RECORD_DURATION = 2 * 60; // 2 minutes

const securityStyles = `
  .interview-secure * { -webkit-user-select: none !important; user-select: none !important; }
  .interview-secure.blurred { filter: blur(20px) !important; transition: filter 0.1s ease; }
  .interview-secure img, .interview-secure video { pointer-events: none; }
  @media print { .interview-secure { display: none !important; } }
`;

export default function InterviewRoom() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // --- States ---
  const [interviewData, setInterviewData] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [stage, setStage] = useState('loading'); // loading, consent, select, ready, starting, interview, complete
  const [error, setError] = useState(null);
  
  // Security
  const [isBlurred, setIsBlurred] = useState(false);
  const [cameraConsent, setCameraConsent] = useState(false);
  
  // Media & Recording
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [videoRecordingProgress, setVideoRecordingProgress] = useState(0);
  const [videoUploaded, setVideoUploaded] = useState(false);
  
  // Audio / VAD / Speaking
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Network / retry state
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState(getNetworkQuality());
  
  // Timer
  const [elapsedTime, setElapsedTime] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);

  // --- Refs ---
  const videoRef = useRef(null);
  const videoStreamRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  const audioPlayerRef = useRef(null);
  
  // VAD & Voice Recording Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const speakingStartTimeRef = useRef(0);
  const lastSpeechTimeRef = useRef(0);
  
  const timerRef = useRef(null);
  const isUploadingRef = useRef(false);
  const videoStartTimeRef = useRef(null);

  // --- 1. Security (Anti-Cheat) ---
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = securityStyles;
    document.head.appendChild(styleSheet);
    
    const handleVisibilityChange = () => document.hidden ? setIsBlurred(true) : setTimeout(() => setIsBlurred(false), 500);
    const handleWindowBlur = () => setIsBlurred(true);
    const handleWindowFocus = () => setTimeout(() => setIsBlurred(false), 300);
    const preventDefault = (e) => e.preventDefault();
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('contextmenu', preventDefault);
    
    return () => {
      document.head.removeChild(styleSheet);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('contextmenu', preventDefault);
    };
  }, []);

  // --- 2. Initial Setup ---
  useEffect(() => {
    validateToken();
    return () => {
      stopAllMedia();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [token]);

  // Watch network quality (Network Information API)
  useEffect(() => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return;
    const update = () => setConnectionQuality(conn.effectiveType || "unknown");
    conn.addEventListener?.("change", update);
    return () => conn.removeEventListener?.("change", update);
  }, []);

  // Warn once if slow network detected before starting the interview
  useEffect(() => {
    if (stage === "ready" && isSlowNetwork()) {
      toast.info(
        "Connexion lente détectée. L'entretien va s'adapter automatiquement. Merci de rester connecté(e).",
        { duration: 6000 }
      );
    }
  }, [stage]);

  // Ensure video stream persists during re-renders
  useEffect(() => {
    if (videoRef.current && videoStreamRef.current) {
      videoRef.current.srcObject = videoStreamRef.current;
    }
  }, [stage, isCameraActive]);

  const validateToken = async () => {
    try {
      const response = await robustFetch(`${API}/api/interview/validate/${token}`, {
        retries: 4,
        onRetry: (n) => {
          setIsRetrying(true);
          setRetryAttempt(n);
        },
      });
      setIsRetrying(false);
      if (!response.ok) {
        let msg = 'Lien invalide ou expiré';
        try {
          const err = await response.json();
          if (err.detail) msg = err.detail;
        } catch (_) {}
        throw new Error(msg);
      }
      const data = await response.json();
      setInterviewData(data);
      if (data.candidates.length === 1) setSelectedCandidate(data.candidates[0].candidate_id);
      setStage('consent');
    } catch (error) {
      setIsRetrying(false);
      setError(error.message || "Ce lien d'entretien n'est plus valide.");
      setStage('expired');
    }
  };

  const stopAllMedia = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current) audioContextRef.current.close();
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  // --- 3. Camera & Audio Initialization ---
  const initializeCamera = async () => {
    if (!cameraConsent) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 640, height: 480, facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } 
      });
      
      videoStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      
      setIsCameraActive(true);
      setupAudioAnalysis(stream);
      setStage('ready'); // Go to instructions first
    } catch (error) {
      toast.error("Impossible d'accéder à la caméra ou au microphone");
      console.error(error);
    }
  };

  // --- 4. VAD (Voice Activity Detection) ---
  const setupAudioAnalysis = (stream) => {
    audioContextRef.current = new AudioContext();
    analyserRef.current = audioContextRef.current.createAnalyser();
    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyserRef.current);
    analyserRef.current.fftSize = 512;
    
    // VAD Parameters
    const VOICE_THRESHOLD = 0.02; // Lowered sensitivity to detect normal speech better
    const SILENCE_DURATION = 2000; // 2 seconds silence to confirm end
    const MIN_SPEECH_DURATION = 500; // Ignore noises < 0.5s

    const updateAnalysis = () => {
      if (!analyserRef.current) return;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate volume
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalizedVolume = average / 255;
      setAudioLevel(normalizedVolume);

          // --- VAD Logic (VISUAL ONLY) ---
          if (stageRef.current === 'interview' && !isAiSpeakingRef.current && !isProcessingResponseRef.current) {
            if (normalizedVolume > VOICE_THRESHOLD) {
              if (!isUserSpeakingRef.current) {
                isUserSpeakingRef.current = true;
                setIsUserSpeaking(true);
              }
            } else {
              // Silence detected - just turn off visual indicator
              if (isUserSpeakingRef.current) {
                 // Debounce visual turn off slightly
                 setTimeout(() => {
                     // Check again if still silent (simplification: just turn off)
                     // In a real loop we'd check current volume, but for UI it's fine.
                 }, 500); 
              }
            }
          }
      
      // Interrupt Logic REMOVED as requested by user
      // if (isAiSpeakingRef.current && normalizedVolume > (VOICE_THRESHOLD * 1.5)) { ... }

      animationFrameRef.current = requestAnimationFrame(updateAnalysis);
    };
    
    updateAnalysis();
  };

  // Refs for VAD loop access without dependencies
  const isAiSpeakingRef = useRef(false);
  const isUserSpeakingRef = useRef(false);
  const isProcessingResponseRef = useRef(false);
  const stageRef = useRef('loading');
  const startedRef = useRef(false); // guard against double startInterview() in StrictMode

  // Sync refs with state
  useEffect(() => { isAiSpeakingRef.current = isAiSpeaking; }, [isAiSpeaking]);
  useEffect(() => { isProcessingResponseRef.current = isProcessingResponse; }, [isProcessingResponse]);
  useEffect(() => { stageRef.current = stage; }, [stage]);

  // Trigger startInterview() when we enter the intermediate 'start_logic' stage.
  // Using useEffect (not calling during render) prevents double calls in StrictMode
  // and avoids setState-during-render warnings.
  useEffect(() => {
    if (stage === 'start_logic' && !startedRef.current) {
      startedRef.current = true;
      startInterview();
    }
  }, [stage]);

  // --- 5. Voice Recording & Processing ---
  const startVoiceRecording = () => {
    if (!videoStreamRef.current) return;
    
    audioChunksRef.current = [];
    try {
      const audioStream = new MediaStream(videoStreamRef.current.getAudioTracks());
      mediaRecorderRef.current = new MediaRecorder(audioStream); // Default codec
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.start(100);
    } catch (e) {
      console.error("Mic record error", e);
    }
  };

  const stopVoiceRecordingAndSend = () => {
    // Stop recording and send whatever we have
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        // Always try to process, don't check for empty/short here, let backend handle or ignore
        await processResponse(audioBlob);
      };
      mediaRecorderRef.current.stop();
    } else if (audioChunksRef.current.length > 0) {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      processResponse(audioBlob);
      audioChunksRef.current = [];
    } else {
        // If nothing recorded, maybe just restart recording? 
        // Or user clicked too fast.
        // Let's try to start recording if it wasn't running
        startVoiceRecording();
        toast.info("Enregistrement démarré. Parlez maintenant.");
    }
  };

  const processResponse = async (audioBlob) => {
    setIsProcessingResponse(true);
    setIsRetrying(false);
    setRetryAttempt(0);
    
    try {
      // Combined STT + Interact Endpoint for lower latency
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');
      formData.append('session_id', sessionIdRef.current);
      
      const res = await robustFetch(`${API}/api/interview/v2/interact`, {
        method: 'POST',
        body: formData,
        retries: 5,
        onRetry: (n) => {
          setIsRetrying(true);
          setRetryAttempt(n);
        },
      });
      setIsRetrying(false);
      
      if (!res.ok) throw new Error('Interaction failed');
      const data = await res.json();
      
      // Update UI immediately (removes perceived latency)
      setCurrentQuestion(data.message);
      setQuestionCount(p => p + 1);
      setIsProcessingResponse(false); // hide thinking spinner as soon as we have text
      
      // Speak AI response
      await speakAiResponse(data.message);
      
      if (data.interview_complete) {
        await completeInterview();
      }
      
    } catch (e) {
      console.error(e);
      const msg = e.name === 'AbortError'
        ? "Réseau trop lent. Vérifiez votre connexion et réessayez."
        : "Erreur de communication. Merci de réessayer.";
      toast.error(msg);
      setIsRetrying(false);
      // Restart mic so the candidate can retry without touching the UI
      if (!isAiSpeaking) {
        setTimeout(() => startVoiceRecording(), 800);
      }
    } finally {
      setIsProcessingResponse(false);
    }
  };

  const speakAiResponse = async (text) => {
    setIsAiSpeaking(true);
    
    try {
      const res = await robustFetch(`${API}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        retries: 3,
      });
      
      if (!res.ok) throw new Error('TTS failed');
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      
      if (audioPlayerRef.current) {
        audioPlayerRef.current.src = url;
        await audioPlayerRef.current.play();
        
        return new Promise(resolve => {
          audioPlayerRef.current.onended = () => {
            setIsAiSpeaking(false);
            // START RECORDING IMMEDIATELY AFTER AI FINISHES
            startVoiceRecording();
            resolve();
          };
        });
      }
    } catch (e) {
      console.error(e);
      // Fallback: Browser TTS (works fully offline, no network needed)
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.onend = () => {
          setIsAiSpeaking(false);
          startVoiceRecording();
      };
      window.speechSynthesis.speak(utterance);
    }
  };

  const stopAiSpeech = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
    }
    window.speechSynthesis.cancel();
    setIsAiSpeaking(false);
  };

  // --- 6. Video Recording & Upload (Progressive) ---
  const startVideoRecording = () => {
    if (!videoStreamRef.current) return;
    
    console.log("Starting progressive video recording...");
    videoChunksRef.current = [];
    
    try {
      videoRecorderRef.current = new MediaRecorder(videoStreamRef.current, { mimeType: 'video/webm;codecs=vp9' });
    } catch (e) {
      videoRecorderRef.current = new MediaRecorder(videoStreamRef.current);
    }
    
    let chunkIndex = 0;
    
    videoRecorderRef.current.ondataavailable = async (e) => {
      if (e.data.size > 0) {
        // Upload chunk immediately (with retry for weak networks)
        const idx = chunkIndex++;
        console.log(`Uploading chunk ${idx}, size: ${e.data.size}`);
        
        try {
            const formData = new FormData();
            formData.append('session_id', sessionIdRef.current);
            formData.append('chunk_index', idx);
            formData.append('chunk_data', e.data);
            
            await robustFetch(`${API}/api/interview/video/chunk`, {
                method: 'POST',
                body: formData,
                retries: 3, // keep video upload best-effort; interview must not block on it
            });
        } catch (err) {
            // Silent — video is a nice-to-have. Never block the interview.
            console.error("Chunk upload failed after retries", err);
        }
      }
    };
    
    videoRecorderRef.current.onstop = finalizeVideoUpload;
    
    // Request data every 5 seconds
    videoRecorderRef.current.start(5000); 
    setIsRecordingVideo(true);
    videoStartTimeRef.current = Date.now();
    
    // Stop after duration
    setTimeout(() => {
      if (videoRecorderRef.current?.state === 'recording') {
        console.log("Video duration reached, stopping...");
        videoRecorderRef.current.stop();
      }
    }, VIDEO_RECORD_DURATION * 1000);
  };

  const finalizeVideoUpload = async () => {
    isUploadingRef.current = true;
    setIsRecordingVideo(false);
    console.log("Finalizing video upload...");
    const toastId = toast.loading("Finalisation de la vidéo...", { description: "Assemblage en cours..." });
    
    try {
        const formData = new FormData();
        formData.append('session_id', sessionIdRef.current);
        
        const res = await robustFetch(`${API}/api/interview/video/finalize`, {
            method: 'POST',
            body: formData,
            retries: 3,
        });
        
        if (res.ok) {
            setVideoUploaded(true);
            toast.success("Vidéo sauvegardée et disponible !", { id: toastId });
        } else {
            const err = await res.json();
            console.error("Finalize error:", err);
            toast.error("Erreur assemblage vidéo", { id: toastId });
        }
    } catch (e) {
        console.error("Finalize network error", e);
        toast.error("Erreur réseau vidéo", { id: toastId });
    } finally {
        isUploadingRef.current = false;
    }
  };

  // Deprecated single upload function
  const uploadVideo = async (blob) => {}; 
  const uploadVideoChunks = () => {}; 


  // --- 7. Interview Flow Control ---
  const startInterview = async () => {
    setStage('starting');
    try {
      // Use language from interviewData if available (passed from validateToken)
      const lang = interviewData?.link_type === 'bilingual' ? 'fr' : (interviewData?.interview_language || 'fr');
      
      const res = await robustFetch(`${API}/api/interview/v2/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, candidate_id: selectedCandidate, language: lang }),
        retries: 5,
        onRetry: (n) => {
          setIsRetrying(true);
          setRetryAttempt(n);
        },
      });
      setIsRetrying(false);
      
      if (!res.ok) throw new Error('Start failed');
      const data = await res.json();
      
      setSessionId(data.session_id);
      sessionIdRef.current = data.session_id;
      setCurrentQuestion(data.message);
      setQuestionCount(1);
      setStage('interview');
      
      // Start timers & recording
      timerRef.current = setInterval(() => setElapsedTime(p => p + 1), 1000);
      startVideoRecording();
      
      // Speak Intro
      await speakAiResponse(data.message);
      
    } catch (e) {
      toast.error("Erreur démarrage");
      setStage('ready');
    }
  };

  const completeInterview = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    // Wait for the full 2-minute video duration before stopping
    if (isRecordingVideo && videoRecorderRef.current && videoRecorderRef.current.state === 'recording') {
        const elapsed = Date.now() - (videoStartTimeRef.current || Date.now());
        const remaining = (VIDEO_RECORD_DURATION * 1000) - elapsed;
        
        if (remaining > 0) {
            toast.info(`Enregistrement vidéo en cours... ${Math.ceil(remaining / 1000)}s restantes`);
            await new Promise(resolve => setTimeout(resolve, remaining));
        }
        
        // Now stop the recorder (the setTimeout in startVideoRecording may have already stopped it)
        if (videoRecorderRef.current?.state === 'recording') {
            console.log("Stopping video recording for completion...");
            const uploadPromise = new Promise((resolve) => {
                videoRecorderRef.current.onstop = () => {
                    finalizeVideoUpload();
                    const check = setInterval(() => {
                        if (!isUploadingRef.current) { clearInterval(check); resolve(); }
                    }, 500);
                };
                videoRecorderRef.current.stop();
            });
            toast.info("Finalisation de l'enregistrement vidéo...");
            await uploadPromise;
        }
    }
    
    // Wait for any pending upload
    if (isUploadingRef.current) {
        toast.info("Upload en cours... Veuillez patienter.");
        await new Promise(r => {
            const check = setInterval(() => {
                if (!isUploadingRef.current) { clearInterval(check); r(); }
            }, 500);
        });
    }
    
    try {
      const res = await robustFetch(`${API}/api/interview/complete/${sessionIdRef.current}`, {
        method: 'POST',
        retries: 5,
      });
      const data = await res.json();
      console.log("Complete response:", data);
      if (!res.ok) {
        console.error("Complete failed:", data);
        toast.error("Erreur lors de la finalisation. Merci de réessayer.");
      }
    } catch (e) { 
      console.error("Complete error:", e);
      toast.error("Réseau instable. Vos réponses sont enregistrées, le rapport sera généré côté serveur.");
    }
    
    setStage('complete');
  };

  // --- RENDER ---
  if (stage === 'loading') return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-10 h-10 text-indigo-500 animate-spin" /></div>;
  
  if (stage === 'expired') {
    return <LinkExpired reason={error} />;
  }
  
  if (stage === 'consent') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Bienvenue</h1>
            <div className="bg-zinc-800 p-6 rounded-lg mb-6 text-left flex items-start gap-4 border border-zinc-700">
              <Checkbox 
                checked={cameraConsent} 
                onCheckedChange={setCameraConsent} 
                id="consent" 
                className="w-6 h-6 border-2 border-zinc-500 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
              />
              <label htmlFor="consent" className="text-white text-base cursor-pointer font-medium leading-relaxed">
                J'autorise l'accès à ma caméra et à mon microphone pour cet entretien.
              </label>
            </div>
            <Button onClick={initializeCamera} disabled={!cameraConsent} className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-lg">
              Activer Caméra & Micro
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (stage === 'ready') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 space-y-6">
            <h1 className="text-2xl font-bold text-white text-center">Instructions</h1>
            
            <div className="space-y-4">
              <div className="flex gap-4 items-start bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="bg-indigo-500/20 p-2 rounded-full text-indigo-400 mt-1">
                  <Volume2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Écoutez Ema</h3>
                  <p className="text-sm text-zinc-400">Elle vous posera des questions une par une.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="bg-green-500/20 p-2 rounded-full text-green-400 mt-1">
                  <Volume2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Répondez naturellement</h3>
                  <p className="text-sm text-zinc-400">Parlez clairement face à votre caméra.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="bg-lime-500/20 p-2 rounded-full text-lime-400 mt-1">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Envoyez votre réponse</h3>
                  <p className="text-sm text-zinc-400">Quand vous avez fini, cliquez sur le bouton <strong>ENVOYER</strong>.</p>
                </div>
              </div>
            </div>

            <Button onClick={() => setStage(interviewData.candidates.length > 1 ? 'select' : 'start_logic')} className="w-full bg-white text-black hover:bg-zinc-200 h-12 text-lg font-semibold mt-4">
              J'ai compris, commencer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Intermediate state: trigger start logic via useEffect (not during render)
  // to avoid double-invocation in React StrictMode and side-effects during render.
  if (stage === 'start_logic') {
      return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-10 h-10 text-indigo-500 animate-spin" /></div>;
  }

  if (stage === 'select') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="max-w-xl w-full space-y-6">
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-zinc-800">
            <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 h-1 bg-zinc-800 rounded"><div className="h-full bg-green-500" style={{width: `${audioLevel*100}%`}}/></div>
          </div>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              {interviewData?.candidates.length > 1 && (
                <Select value={selectedCandidate} onValueChange={setSelectedCandidate}>
                  <SelectTrigger className="mb-4 bg-zinc-800 border-zinc-700"><SelectValue placeholder="Choisir profil" /></SelectTrigger>
                  <SelectContent>{interviewData.candidates.map(c => <SelectItem key={c.candidate_id} value={c.candidate_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button onClick={startInterview} disabled={stage === 'starting' || !selectedCandidate} className="w-full bg-indigo-600 hover:bg-indigo-700">
                {stage === 'starting' ? <Loader2 className="animate-spin" /> : "Commencer l'entretien"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (stage === 'interview') {
    return (
      <div className={`min-h-screen bg-zinc-950 flex flex-col interview-secure ${isBlurred ? 'blurred' : ''}`}>
        <audio ref={audioPlayerRef} className="hidden" />
        
        {/* Header */}
        <div className="bg-zinc-900 border-b border-zinc-800 p-4 flex justify-between items-center">
          <img src={EmaLogo} alt="Ema" className="h-8 w-auto" />
          <div className="flex gap-3">
            {isRecordingVideo && <Badge variant="destructive" className="animate-pulse">REC</Badge>}
            <Badge variant="outline" className="text-zinc-400">{Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}</Badge>
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 gap-8">
          {/* Video Preview with Overlay Button */}
          <div className="relative w-full max-w-lg aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-zinc-800">
            <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            
            {/* Status Indicators */}
            <div className="absolute top-3 right-3 flex flex-col gap-2">
              {isUserSpeaking && <Badge className="bg-green-500 hover:bg-green-600 animate-pulse">🎙 Listening</Badge>}
              {isAiSpeaking && <Badge className="bg-indigo-500 hover:bg-indigo-600">🔊 Ema Speaking</Badge>}
              {isProcessingResponse && (
                <Badge className="bg-amber-500 hover:bg-amber-600 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Thinking...
                </Badge>
              )}
            </div>
            
            {/* Audio Viz */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
              <div className="h-full bg-green-500 transition-all duration-75" style={{width: `${audioLevel * 100}%`}} />
            </div>

            {/* Manual Send Button - OVERLAY on Video for Visibility */}
            {!isAiSpeaking && !isProcessingResponse && (
              <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center pb-4">
                <Button 
                  onClick={stopVoiceRecordingAndSend}
                  size="lg"
                  className="rounded-full px-8 shadow-xl border-2 transition-all transform hover:scale-105 active:scale-95 bg-lime-500 hover:bg-lime-400 text-black border-lime-400 font-bold text-lg h-14 w-auto"
                >
                  <Zap className="w-5 h-5 mr-2 fill-current" />
                  ENVOYER
                </Button>
                <p className="text-xs text-white/80 mt-2 font-medium bg-black/50 px-2 py-1 rounded backdrop-blur-sm">
                  Appuyez sur envoyer si tu finis de parler
                </p>
              </div>
            )}
          </div>

          {/* AI Message */}
          <div className="max-w-2xl text-center space-y-4">
            {isProcessingResponse ? (
              <div className="flex flex-col items-center gap-3" data-testid="ema-thinking">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2.5 h-2.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2.5 h-2.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <p className="text-sm text-indigo-300">
                  {isRetrying
                    ? `Réseau lent — reconnexion (tentative ${retryAttempt})...`
                    : "Ema prépare sa réponse..."}
                </p>
              </div>
            ) : (
              <p className="text-xl md:text-2xl text-white font-medium leading-relaxed">
                "{currentQuestion}"
              </p>
            )}
            
            <p className="text-sm text-zinc-500 animate-pulse">
              {isAiSpeaking ? "Ema parle..." : isUserSpeaking ? "Je vous écoute..." : "Parlez quand vous êtes prêt..."}
            </p>

            {/* Network quality badge (only shown if slow) */}
            {(connectionQuality === "2g" || connectionQuality === "slow-2g" || connectionQuality === "3g") && (
              <p className="text-xs text-amber-400/80 mt-2" data-testid="net-quality-warning">
                Connexion {connectionQuality.toUpperCase()} détectée — mode adaptatif activé
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'complete') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 p-8 text-center max-w-md w-full">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl text-white font-bold mb-2">Entretien Terminé</h1>
          <p className="text-zinc-400">Merci ! Vos réponses ont été enregistrées.</p>
        </Card>
      </div>
    );
  }

  return null;
}
